// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, db } from "../config/firebase";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { normalizarEmpresaRef } from "../utils/empresaScope";
import { PERMISSOES_DEFAULT } from "../config/permissoesDefault";
import { mergePermissoes } from "../utils/permissoesOverrides";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [usuario, setUsuario] = useState(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (!fbUser) {
        setUsuario(null);
        setCarregando(false);
        return;
      }

      try {
        // Busca o documento do usuário no Firestore: /usuarios/{uid}
        const ref = doc(db, "usuarios", fbUser.uid);
        const snap = await getDoc(ref);

        let perfil = {};
        if (snap.exists()) {
          perfil = { id: snap.id, ...snap.data() };
        }

        // Resolve grupoNome (preferir o nome salvo no usuário; se não existir, tentar buscar em /grupos/{grupoId})
        let grupoNome = perfil.grupoNome || perfil.grupo || null;
        if (!grupoNome && perfil.grupoId) {
          try {
            const gRef = doc(db, "grupos", perfil.grupoId);
            const gSnap = await getDoc(gRef);
            if (gSnap.exists()) {
              const gData = gSnap.data() || {};
              grupoNome = gData.nome || gData.grupoNome || null;
            }
          } catch (e) {
            // não bloqueia login se falhar
            console.warn("Não foi possível resolver grupoNome:", e);
          }
        }


        // Normaliza empresasPermitidas: aceita string (nome) ou objeto { id, nome }
        const empresasPermitidasRaw = Array.isArray(perfil.empresasPermitidas)
          ? perfil.empresasPermitidas
          : [];
        const empresasPermitidas = empresasPermitidasRaw
          .map((e) => normalizarEmpresaRef(e))
          .filter(Boolean);

        // Permissões por grupo (overrides) — opcional
        let permissoesOverride = {};
        let permissoesAtivas = PERMISSOES_DEFAULT;

        // Overrides individuais por usuário (opcional)
        const permissoesUserOverrides =
          perfil.permissoesUserOverrides ||
          perfil.permissoesUsuarioOverrides ||
          perfil.permissoesOverridesUsuario ||
          {};

        if (perfil.grupoId) {
          try {
            const pRef = doc(db, "grupos", perfil.grupoId, "config", "permissoes");
            const pSnap = await getDoc(pRef);
            if (pSnap.exists()) {
              const pData = pSnap.data() || {};
              permissoesOverride = pData.overrides || {};
            } else {
              permissoesOverride = {};
            }
            permissoesAtivas = mergePermissoes(PERMISSOES_DEFAULT, permissoesOverride);
          } catch (e) {
            console.warn("Não foi possível carregar permissões do grupo:", e);
            permissoesOverride = {};
            permissoesAtivas = PERMISSOES_DEFAULT;
          }
        }

        if (permissoesUserOverrides && typeof permissoesUserOverrides === "object") {
          permissoesAtivas = mergePermissoes(permissoesAtivas, permissoesUserOverrides);
        }

        setUsuario({
          uid: fbUser.uid,
          email: fbUser.email,
          displayName: fbUser.displayName ?? perfil.nome ?? "",
          ...perfil,
          grupoNome: grupoNome || perfil.grupoNome || perfil.grupo || null,
          empresasPermitidas, // aqui entra "nivel", "grupoId", "empresaId", etc. se existirem
          permissoesOverride,
          permissoesAtivas,
          permissoesUserOverrides,
        });
      } catch (erro) {
        console.error("Erro ao carregar perfil do usuário:", erro);
        // Pelo menos garante que o usuário logado existe
        setUsuario({
          uid: fbUser.uid,
          email: fbUser.email,
          displayName: fbUser.displayName ?? "",
        });
      } finally {
        setCarregando(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Mantém as permissões do grupo sincronizadas em tempo real (quando o Gestor alterar em Opções)
  useEffect(() => {
    if (!usuario?.grupoId) return;

    const ref = doc(db, "grupos", usuario.grupoId, "config", "permissoes");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const overrides = snap.exists() ? (snap.data()?.overrides || {}) : {};
        setUsuario((u) => {
          if (!u) return u;
          const userOv =
            u.permissoesUserOverrides ||
            u.permissoesUsuarioOverrides ||
            u.permissoesOverridesUsuario ||
            {};
          let ativas = mergePermissoes(PERMISSOES_DEFAULT, overrides);
          if (userOv && typeof userOv === "object") {
            ativas = mergePermissoes(ativas, userOv);
          }
          return { ...u, permissoesOverride: overrides, permissoesAtivas: ativas };
        });
      },
      (err) => {
        console.warn("Falha ao sincronizar permissões do grupo:", err);
      }
    );

    return () => unsub();
  }, [usuario?.grupoId]);

  // Mantém overrides individuais por usuário sincronizados (quando o Gestor alterar em Opções > Permissões)
  useEffect(() => {
    if (!usuario?.uid) return;

    const ref = doc(db, "usuarios", usuario.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const userOv =
          data.permissoesUserOverrides ||
          data.permissoesUsuarioOverrides ||
          data.permissoesOverridesUsuario ||
          {};
        setUsuario((u) => {
          if (!u) return u;
          const grupoOv = u.permissoesOverride || {};
          let ativas = mergePermissoes(PERMISSOES_DEFAULT, grupoOv);
          if (userOv && typeof userOv === "object") {
            ativas = mergePermissoes(ativas, userOv);
          }
          return {
            ...u,
            permissoesUserOverrides: userOv,
            permissoesAtivas: ativas,
          };
        });
      },
      (err) => {
        console.warn("Falha ao sincronizar permissões do usuário:", err);
      }
    );

    return () => unsub();
  }, [usuario?.uid]);

  const login = async (email, senha) => {
    // Evita o "login só na segunda tentativa": existe uma janela curta entre
    // o resolve do signIn e o onAuthStateChanged atualizar o contexto.
    // Se o usuário navegar nesse intervalo, o PrivateRoute pode redirecionar de volta.
    setCarregando(true);
    return signInWithEmailAndPassword(auth, email, senha);
  };

  const logout = async () => {
    setCarregando(true);
    return signOut(auth);
  };

  const value = { usuario, carregando, login, logout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
